import { media } from 'atoms/breakpoints/breakpoints';
import color from 'atoms/colors/colors';

// Quarks
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

// Types
import type { ContentfulComponentCard } from 'graphqlTypes';
import type { FC } from 'react';

interface BasicCardProps
  extends FlexProps,
    Omit<ContentfulComponentCard, 'id' | 'children' | 'contentful_id' | 'internal' | 'node_locale'> {
  backgroundIsDark: boolean;
}

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BasicCard: FC<BasicCardProps> = ({ link, featuredImage, heading, callToAction, body, ...props }) => (
  <Link href={link || ''} textDecoration="none" width="100%">
    <Flex
      position="relative"
      width="100%"
      height="100%"
      maxHeight="270px"
      flexDirection="column"
      borderRadius="16px"
      overflowX="hidden"
      overflowY="hidden"
      cursor="pointer"
      boxShadow="xxl"
      md={{ maxHeight: '370px' }}
      css={`
        &:before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          bottom: 0;
          left: 0;
          z-index: 1;
          opacity: 1;
          transition: opacity 300ms ease-in-out;
          background: linear-gradient(180deg, rgba(0, 0, 0, 0) 57.88%, #000000 105.5%);
        }
        &:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          bottom: 0;
          left: 0;
          z-index: 1;
          opacity: 0;
          transition: opacity 300ms ease-in-out;
          background: linear-gradient(0deg, rgba(0, 0, 0, 0.43), rgba(0, 0, 0, 0.4));
        }
        &:hover {
          &:before {
            opacity: 0;
          }
          &:after {
            opacity: 1;
          }
          .card-item {
            top: 0%;
            ${media.lg} {
              top: 36%;
            }
            .card-body {
              opacity: 1;
            }
          }
        }
      `}
      {...props}
    >
      {(featuredImage?.imageDesktop || featuredImage?.bynderImageDesktop) && (
        <Image
          image={(featuredImage?.bynderImageDesktop || featuredImage.imageDesktop) as ImageData}
          alt={featuredImage?.altText || 'Card Image'}
          boxShadow={featuredImage?.boxShadow}
          objectFit="cover"
          objectPosition="center"
          width="100%"
          height="100%"
          roundedCorners={featuredImage?.roundedCorners}
        />
      )}
      <Flex
        className="card-item"
        position="absolute"
        zIndex={2}
        top="calc(100% - 96px)"
        transition="400ms ease-in-out"
        left={0}
        flexDirection="column"
        paddingAll={32}
      >
        <Flex flexDirection="column" gap="12px">
          {heading && (
            <Heading
              as="h4"
              fontSize="h5"
              lineHeight="h5"
              fontWeight="bold"
              fontFamily="secondaryFont"
              textColor="common-white"
              css={textWrapCSS}
            >
              {heading}
            </Heading>
          )}
          {body && (
            <Flex
              className="card-body"
              opacity={0}
              flexDirection="column"
              justifyContent="space-between"
              gap="16px"
              width="100%"
              fontSize="textMd"
              lineHeight="textMd"
              textColor="common-white"
              css={textWrapCSS}
              transition="400ms ease-in-out"
            >
              {richTextParser(body)}
            </Flex>
          )}
          {callToAction && (
            <Flex
              css={`
                .MuiButton-root {
                  width: fit-content !important;
                  color: ${color.common.white};
                }
              `}
            >
              <ComponentButton key={callToAction?.contentful_id} {...callToAction} />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  </Link>
);

export default BasicCard;
